import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Send, Paperclip } from "lucide-react";
import { municipalitiesByIsland, needOptions, business } from "../content/site";
import { trackEvent } from "../lib/analytics";
import { useLp } from "../i18n/lang";
import { useCopy } from "../i18n/copy";
import { useNeedLabels } from "../i18n/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-white border border-slate-300 rounded-sm px-4 py-3 text-[15px] text-ink placeholder:text-slate-500 focus:border-cadastral focus:outline-none focus:ring-1 focus:ring-cadastral transition-colors";

export const ContactForm = ({ defaultNeed = "" }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const [started, setStarted] = useState(false);
  const [need, setNeed] = useState(defaultNeed || params.get("necesidad") || "");
  const f = useCopy("form");
  const cm = useCopy("common");
  const needLabels = useNeedLabels();
  const lp = useLp();

  const markStart = () => {
    if (!started) {
      setStarted(true);
      trackEvent("form_start");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    const form = e.target;
    const data = new FormData(form);
    data.set("necesidad", need);
    data.set("privacidad", form.privacidad.checked ? "true" : "");
    try {
      const res = await fetch(`${API}/leads`, { method: "POST", body: data });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || f.error);
      }
      trackEvent("form_submit", { necesidad: need });
      if (data.get("referencia_catastral")) trackEvent("reference_cadastral_submit");
      if (fileName) trackEvent("document_upload");
      navigate(lp("/gracias/"));
    } catch (err) {
      setError(err.message);
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5" noValidate={false}>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="cf-nombre" className="block text-sm font-medium text-ink mb-1.5">{f.name} *</label>
          <input id="cf-nombre" name="nombre" required minLength={2} maxLength={120} onFocus={markStart}
                 data-testid="contact-form-name" className={inputClass} placeholder={f.namePh} autoComplete="name" />
        </div>
        <div>
          <label htmlFor="cf-telefono" className="block text-sm font-medium text-ink mb-1.5">{f.phone} *</label>
          <input id="cf-telefono" name="telefono" type="tel" required pattern="[+0-9][0-9 .\-]{7,16}"
                 data-testid="contact-form-phone" className={inputClass} placeholder="600 000 000" autoComplete="tel" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="cf-email" className="block text-sm font-medium text-ink mb-1.5">{f.email} *</label>
          <input id="cf-email" name="email" type="email" required maxLength={160}
                 data-testid="contact-form-email" className={inputClass} placeholder="tu@email.com" autoComplete="email" />
        </div>
        <div>
          <label htmlFor="cf-municipio" className="block text-sm font-medium text-ink mb-1.5">{f.municipality} *</label>
          <select id="cf-municipio" name="municipio" required defaultValue=""
                  data-testid="contact-form-municipality" className={inputClass}>
            <option value="" disabled>{f.municipalityPh}</option>
            {municipalitiesByIsland.map((group) => (
              <optgroup key={group.island} label={group.island}>
                {group.towns.map((m) => <option key={m} value={m}>{m}</option>)}
              </optgroup>
            ))}
            <option value="Otro (fuera de Canarias)">{f.other}</option>
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="cf-necesidad" className="block text-sm font-medium text-ink mb-1.5">{f.need} *</label>
        <select id="cf-necesidad" name="necesidad" required value={need} onChange={(e) => setNeed(e.target.value)}
                data-testid="contact-form-need-type" className={inputClass}>
          <option value="" disabled>{f.needPh}</option>
          {needOptions.map((n, i) => <option key={n} value={n}>{needLabels[i] || n}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="cf-mensaje" className="block text-sm font-medium text-ink mb-1.5">{f.message} *</label>
        <textarea id="cf-mensaje" name="mensaje" required minLength={10} maxLength={4000} rows={4}
                  data-testid="contact-form-message" className={inputClass}
                  placeholder={f.messagePh} />
      </div>
      <div className="grid sm:grid-cols-2 gap-5 sm:items-end">
        <div>
          <label htmlFor="cf-refcat" className="block text-sm font-medium text-ink mb-1.5">
            {f.refcat} <span className="text-slate-600 font-normal">{f.optional}</span>
          </label>
          <input id="cf-refcat" name="referencia_catastral" maxLength={40}
                 data-testid="contact-form-catastral-ref" className={`${inputClass} font-mono`} placeholder="1234567AB1234C0001XX" />
        </div>
        <div>
          <label htmlFor="cf-archivo" className="block text-sm font-medium text-ink mb-1.5">
            {f.attach} <span className="text-slate-600 font-normal">{f.attachHint}</span>
          </label>
          <label htmlFor="cf-archivo" className={`${inputClass} flex items-center gap-2 cursor-pointer text-slate-600`}>
            <Paperclip size={15} aria-hidden="true" />
            <span className="truncate">{fileName || f.chooseFile}</span>
          </label>
          <input id="cf-archivo" name="archivo" type="file" accept=".pdf,.jpg,.jpeg,.png" className="sr-only"
                 data-testid="contact-form-file" onChange={(e) => setFileName(e.target.files[0]?.name || "")} />
        </div>
      </div>
      <div className="hidden" aria-hidden="true">
        <label htmlFor="cf-website">{f.honeypot}</label>
        <input id="cf-website" name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>
      <p className="text-xs text-slate-600 leading-relaxed bg-paper-tint border hairline rounded-sm px-4 py-3" data-testid="contact-form-privacy-layer">
        <strong className="text-ink">{f.privLead}</strong> {f.privResponsible} {business.legalName} (NIF {business.nif}) ·
        {" "}{f.privPurpose} ·
        {" "}{f.privLegit} ·
        {" "}{f.privRecipients} ·
        {" "}{f.privRights} ·
        {" "}{f.privMore}{" "}
        <Link to={lp("/politica-de-privacidad/")} className="link-underline">{f.privLink}</Link>.
      </p>
      <div className="flex items-start gap-3">
        <input id="cf-privacidad" name="privacidad" type="checkbox" required
               data-testid="contact-form-privacy" className="mt-1 h-4 w-4 accent-[#C56D42]" />
        <label htmlFor="cf-privacidad" className="text-sm text-slate-600 leading-relaxed">
          {f.accept}{" "}
          <Link to={lp("/politica-de-privacidad/")} className="link-underline">{f.privLink}</Link>. *
        </label>
      </div>
      {error && (
        <p role="alert" data-testid="contact-form-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-4 py-3">
          {error}
        </p>
      )}
      <button type="submit" disabled={sending} data-testid="contact-form-submit" className="btn-primary w-full sm:w-auto disabled:opacity-60">
        {sending ? f.sending : (<>{cm.tellCase} <Send size={15} aria-hidden="true" /></>)}
      </button>
    </form>
  );
};
